<script setup lang="ts">
  defineOptions({ name: 'CPPopover', inheritAttrs: false })

  import type { PropType } from 'vue'

  import { HLPopover } from '@platform-ui/highrise'

  const props = defineProps({
    id: {
      type: String,
      required: true,
    },
    show: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    trigger: {
      type: String as PropType<'click' | 'hover'>,
      required: false,
      default: 'click',
    },
    placement: {
      type: String as PropType<
        | 'top'
        | 'top-start'
        | 'top-end'
        | 'bottom'
        | 'bottom-start'
        | 'bottom-end'
        | 'left'
        | 'left-start'
        | 'left-end'
        | 'right'
        | 'right-start'
        | 'right-end'
      >,
      required: false,
      default: 'bottom',
    },
    showArrow: {
      type: Boolean,
      required: false,
      default: true,
    },
    zIndex: {
      type: Number,
      required: false,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  })

  // Emits definitions
  const emit = defineEmits<{
    'update:show': [show: boolean]
    show: []
    hide: []
  }>()

  // User-defined functions
  const handleUpdateShow = (show: boolean) => {
    emit('update:show', show)
  }

  const handleShow = () => {
    emit('show')
  }

  const handleHide = () => {
    emit('hide')
  }
</script>

<template>
  <HLPopover
    v-bind="$attrs"
    :id="props.id"
    :show="props.show"
    :trigger="props.trigger"
    :placement="props.placement"
    :show-arrow="props.showArrow"
    :z-index="props.zIndex"
    :disabled="props.disabled"
    @update:show="handleUpdateShow"
    @show="handleShow"
    @hide="handleHide"
  >
    <template v-if="$slots.trigger" #trigger>
      <slot name="trigger" />
    </template>
    <template v-if="$slots.footer" #footer>
      <slot name="footer" />
    </template>
    <slot />
  </HLPopover>
</template>

<!--
  HighRise popover overrides — unscoped because HLPopover teleports its
  surface into document.body. <style scoped> and :deep() cannot reach
  teleported nodes. Targets .hr-popover, the floating popover surface.
  HighRise injects --n-* custom properties as inline styles via its JS
  theme engine, so !important is required to beat inline specificity.
-->
<style>
  .hr-popover {
    --n-color: var(--cp-background-500) !important;
    --n-menu-color: var(--cp-background-500) !important;
    --n-text-color: var(--cp-brand-font) !important;
    --n-border: 1px solid var(--cp-border-500) !important;
    border: 1px solid var(--cp-border-500) !important;
  }
</style>
